/* Product-page behaviour. Loaded only on product templates so the rest of the
   store does not pay for it. theme.js is a module too and sits earlier in the
   document, so its body has already run by the time this executes. */
const { formatMoney, syncButtonPrice } = window.theme;

/* ---------- Info popovers on option groups -------------------------------- */
function initOptionInfo() {
  const buttons = document.querySelectorAll('[data-copt-info]');
  if (!buttons.length) return;
  const closeAll = (except) => {
    document.querySelectorAll('[data-copt-pop]').forEach((pop) => {
      if (pop === except) return;
      if (pop.hidden) return;
      pop.hidden = true;
      const btn = pop.closest('[data-copt]').querySelector('[data-copt-info]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  };
  buttons.forEach((btn) => {
    const pop = document.getElementById(btn.getAttribute('aria-controls'));
    if (!pop) return;
    let pinned = false;
    const open = () => { closeAll(pop); pop.hidden = false; btn.setAttribute('aria-expanded', 'true'); };
    const close = () => { pop.hidden = true; btn.setAttribute('aria-expanded', 'false'); };
    /* Click pins the popup open so hovering away no longer dismisses it. */
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      pinned = !pinned;
      pinned ? open() : close();
    });
    /* Hover is a preview, and only where hovering is a real input. */
    if (window.matchMedia('(hover: hover)').matches) {
      btn.addEventListener('mouseenter', () => { if (!pinned) open(); });
      btn.closest('[data-copt]').addEventListener('mouseleave', () => { if (!pinned) close(); });
    }
    pop.addEventListener('click', (e) => e.stopPropagation());
  });
  document.addEventListener('click', () => closeAll());
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });
}
/* ---------- Free-shipping progress + instalments -------------------------- */
function initShipBar() {
  const bar = document.querySelector('[data-ship-bar]');
  if (!bar) return;
  const fill = bar.querySelector('[data-ship-fill]');
  const text = bar.querySelector('[data-ship-text]');
  /* In the buy card the instalment line sits outside the bar, in the meta row. */
  const scope = bar.closest('.bcard') || document;
  const pay = scope.querySelector('[data-ship-pay]');
  const threshold = Number(bar.dataset.threshold) || 0;
  const count = Number(bar.dataset.instalments) || 0;

  const render = (total) => {
    if (!Number.isFinite(total)) return;
    if (threshold > 0) {
      const pct = Math.max(0, Math.min(100, (total / threshold) * 100));
      if (fill) fill.style.setProperty('--pct', pct.toFixed(1) + '%');
      const left = threshold - total;
      bar.dataset.unlocked = left <= 0 ? 'true' : 'false';
      if (text) {
        text.textContent = left <= 0
          ? text.dataset.unlocked
          : text.dataset.progress.replace('[amount]', formatMoney(left));
      }
    }
    if (pay && count > 1) {
      pay.textContent = pay.dataset.tpl.replace('[amount]', formatMoney(Math.round(total / count)));
    }
  };

  document.addEventListener('lot:change', (e) => render(e.detail.total));

  /* No lot block on this product — follow the variant price instead. */
  const fromPrice = () => {
    const el = document.querySelector('[data-add-price]');
    const raw = el && el.textContent.replace(/[^0-9.,]/g, '').replace(/\s/g, '');
    if (!raw) return null;
    const n = Number(raw.replace(/\.(?=\d{3}\b)/g, '').replace(',', '.'));
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };
  if (!document.querySelector('[data-bundle-qty]')) {
    const sync = () => { const v = fromPrice(); if (v !== null) render(v); };
    document.addEventListener('variant:change', () => setTimeout(sync, 60));
    sync();
  }
}
/* ---------- Same-day cut-off countdown ------------------------------------ */
function initCountdown() {
  document.querySelectorAll('[data-countdown]').forEach((el) => {
    const clock = el.querySelector('[data-countdown-clock]');
    const hour = Number(el.dataset.cutoff);
    if (!clock || !Number.isFinite(hour)) return;
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const now = new Date();
      const cut = new Date(now);
      cut.setHours(hour, 0, 0, 0);
      if (cut <= now) cut.setDate(cut.getDate() + 1);
      const s = Math.floor((cut - now) / 1000);
      clock.textContent = `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
    };
    tick();
    setInterval(tick, 1000);
  });
}
function initSpecStrip() {
  /* Same treatment for the swatch grid, which fades only while it can scroll. */
  document.querySelectorAll('.variant-picker__swatches').forEach((g) => {
    const sync = () => {
      const more = g.scrollHeight > g.clientHeight + 2 && g.scrollTop + g.clientHeight < g.scrollHeight - 4;
      g.dataset.more = more ? 'true' : 'false';
    };
    g.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  });
  document.querySelectorAll('.sicons').forEach((ul) => {
    const sync = () => {
      const more = ul.scrollWidth > ul.clientWidth + 2 && ul.scrollLeft + ul.clientWidth < ul.scrollWidth - 4;
      ul.dataset.more = more ? 'true' : 'false';
    };
    ul.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  });
}
/* ---------- Bundle selector: quantity, per-unit colours, button price ----- */
function initBundle() {
  const unitsWrap = document.querySelector('[data-bundle-units]');

  /* Option mode: each pack is a real variant, so picking one just drives the
     variant radio and lets the existing machinery update price, URL and button.
     Nothing to multiply, and the cart charges exactly what the card showed. */
  const optionRows = Array.from(document.querySelectorAll('[data-lot-value]'));
  if (optionRows.length) {
    /* The row already knows what the pack costs, so the button is updated at
       once. Waiting for the variant fetch to bring the new card back left the
       previous pack's price on screen for as long as the network took — and
       indefinitely when it failed. */
    const paint = (row) => {
      const priceEl = document.querySelector('[data-add-price]');
      const total = Number(row.dataset.bundleTotal);
      if (!priceEl || !Number.isFinite(total)) return;
      priceEl.dataset.unit = total;
      priceEl.textContent = formatMoney(total);
    };

    /* Same event the quantity mode emits, so the sticky bar, the free-shipping
       progress and the instalment line all follow the pack without each having
       to parse the button or wait for the fresh card. */
    const announce = (row) => document.dispatchEvent(new CustomEvent('lot:change', {
      detail: {
        total: Number(row.dataset.bundleTotal),
        qty: Number(row.dataset.lotQty || 1),
        full: Number(row.dataset.bundleFull),
        name: row.dataset.lotValue,
      },
    }));

    optionRows.forEach((row) => row.addEventListener('change', () => {
      if (!row.checked) return;
      paint(row);
      announce(row);
      const pos = row.dataset.lotPosition;
      const want = row.dataset.lotValue;
      const input = Array.from(document.querySelectorAll(`variant-picker input[name="option${pos}"]`))
        .find((i) => i.value === want);
      if (!input || input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }));

    /* The fresh card that arrives after a shade change carries the right price
       already; this only covers the case where it never arrives. */
    document.addEventListener('variant:change', () => {
      const row = optionRows.find((r) => r.checked);
      if (row) setTimeout(() => paint(row), 120);
    });

    const start = optionRows.find((r) => r.checked);
    if (start) announce(start);
    syncButtonPrice();
    return;
  }

  const radios = Array.from(document.querySelectorAll('[data-bundle-qty]'));
  if (!radios.length) { syncButtonPrice(); return; }

  /* The variant picker replaces the whole buy-buttons node on every shade
     change, so these two are re-queried rather than captured once — a stale
     reference silently wrote the lot quantity to a detached input. */
  const apply = (r) => {
    const qtyInput = document.querySelector('.buy-buttons input[name="quantity"]');
    const priceEl = document.querySelector('[data-add-price]');
    const qty = Number(r.dataset.bundleQty || 1);
    if (qtyInput) {
      qtyInput.value = qty;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Button shows the lot total, not the unit price
    const total = Number(r.dataset.bundleTotal);
    if (priceEl && Number.isFinite(total)) priceEl.textContent = formatMoney(total);
    if (Number.isFinite(total)) {
      document.dispatchEvent(new CustomEvent('lot:change', {
        detail: { total, qty, full: Number(r.dataset.bundleFull), name: r.dataset.bundleName },
      }));
    }
    // The opt-in only makes sense from two units up
    if (unitsWrap) {
      unitsWrap.hidden = qty < 2;
      syncUnits(qty);
    }
  };

  /* One shade for the whole lot unless the customer asks to mix, in which case
     one picker per extra unit. Hidden radios stay disabled so a collapsed
     picker never posts a line item property. */
  const mixToggle = document.querySelector('[data-unit-toggle]');
  const unitsInner = document.querySelector('[data-bundle-units-in]');

  function syncUnits(qty) {
    const mixing = !!(mixToggle && mixToggle.checked);
    if (unitsInner) unitsInner.hidden = !mixing;
    if (!unitsWrap) return;
    unitsWrap.querySelectorAll('[data-unit-row]').forEach((row) => {
      const n = Number(row.dataset.unitRow);
      const on = mixing && n <= qty;
      row.hidden = !on;
      row.querySelectorAll('input[type="radio"]').forEach((i) => { i.disabled = !on; });
    });
  }

  if (mixToggle) {
    mixToggle.addEventListener('change', () => {
      const r = radios.find((x) => x.checked);
      syncUnits(r ? Number(r.dataset.bundleQty || 1) : 1);
    });
  }

  radios.forEach((r) => r.addEventListener('change', () => { if (r.checked) apply(r); }));

  /* A shade change re-renders the buy card from the server, which knows
     nothing about the chosen lot: without this the button falls back to the
     unit price and the quantity to 1, so the order would be short. */
  document.addEventListener('variant:change', () => {
    const r = radios.find((x) => x.checked);
    if (r) apply(r);
  });

  // Reflect the chosen colour next to each unit label
  document.querySelectorAll('[data-unit-colour]').forEach((input) => {
    input.addEventListener('change', () => {
      if (!input.checked) return;
      const n = input.dataset.unitColour;
      const out = document.querySelector(`[data-unit-selected="${n}"]`);
      if (out) out.textContent = input.value;
    });
  });
  const checked = radios.find((r) => r.checked) || radios[0];
  if (checked) apply(checked);
}


/* Arriving from the subscription page: preselect the subscription option and
   remember the rhythm the visitor picked there. */
function initPlanIntent() {
  const opts = document.querySelector('.popt');
  if (!opts) return;
  let plan = new URLSearchParams(window.location.search).get('plan');
  if (!plan) {
    try { plan = sessionStorage.getItem('subsPlan'); } catch (e) { plan = null; }
  }
  if (!plan) return;

  const sub = opts.querySelectorAll('input[type="radio"]')[1];
  if (sub && !sub.checked) {
    sub.checked = true;
    sub.dispatchEvent(new Event('change', { bubbles: true }));
  }
  const note = opts.querySelector('[data-plan-note]');
  if (note) {
    note.textContent = note.dataset.tpl.replace('[plan]', plan);
    note.hidden = false;
  }
  try { sessionStorage.removeItem('subsPlan'); } catch (e) { /* private mode */ }
}

/* The instalment line stands on its own now that the buy card has no shipping
   bar: it follows the lot total, or the variant price when there is no lot. */
function initInstalments() {
  const el = document.querySelector('[data-instalments]');
  if (!el) return;
  const count = Number(el.dataset.count) || 0;
  if (count < 2) return;

  const render = (total) => {
    if (!Number.isFinite(total)) return;
    el.textContent = el.dataset.tpl.replace('[amount]', formatMoney(Math.round(total / count)));
  };

  document.addEventListener('lot:change', (e) => render(e.detail.total));

  if (!document.querySelector('[data-bundle-qty]')) {
    const fromPrice = () => {
      const p = document.querySelector('[data-add-price]');
      const raw = p && p.textContent.replace(/[^0-9.,]/g, '').replace(/\s/g, '');
      if (!raw) return null;
      const n = Number(raw.replace(/\.(?=\d{3}\b)/g, '').replace(',', '.'));
      return Number.isFinite(n) ? Math.round(n * 100) : null;
    };
    const sync = () => { const v = fromPrice(); if (v !== null) render(v); };
    document.addEventListener('variant:change', () => setTimeout(sync, 60));
    sync();
  }
}

function bootProduct() {
  initPlanIntent();
  initInstalments();
  initOptionInfo();
  initSpecStrip();
  initShipBar();
  initBundle();
  initCountdown();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootProduct);
} else {
  bootProduct();
}
